import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { challengeAPI, getErrorMessage } from '../lib/api';
import {
  Users, Crown, Swords, Share2, Copy, Check, Clock, Trophy, Zap,
  Play, Loader2, RefreshCw, AlertCircle, User, Timer, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../components/LoadingSpinner';

const ChallengeLobby = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [starting, setStarting] = useState(false);
  const [joining, setJoining] = useState(false);

  // Fetch challenge data
const fetchChallenge = useCallback(async () => {
  try {
    const response = await challengeAPI.getByCode(code);
    setChallenge(response.data);
    setError(null);
    
    // If challenge is starting, start countdown
    if (response.data.status === 'starting' && countdown === null) {
      setCountdown(5);
    } else if (response.data.status === 'active' && response.data.has_joined && response.data.user_status !== 'completed') {
      // Only redirect to test if user hasn't completed yet
      navigate(`/challenge/${code}/test`);
    }
  } catch (err) {
    logger.error('Failed to fetch challenge:', err);
    setError(err.response?.data?.detail || 'Challenge not found');
  } finally {
    setLoading(false);
  }
}, [code, navigate, countdown]);

  // Initial load and polling for waiting status
 // Initial load
useEffect(() => {
  fetchChallenge();
}, [fetchChallenge]);

// Separate polling effect
useEffect(() => {
  if (!challenge) return;
  
  // Poll for updates every 3 seconds while waiting
  if (challenge.status === 'waiting' || challenge.status === 'starting') {
    const interval = setInterval(() => {
      fetchChallenge();
    }, 3000);
    
    return () => clearInterval(interval);
  }
}, [challenge?.status, fetchChallenge]);

  // Countdown effect
  useEffect(() => {
    if (countdown === null) return;
    
    if (countdown === 0) {
      if (challenge?.is_creator) {
        // Creator activates challenge and redirects
        challengeAPI.activate(code).then(() => {
          navigate(`/challenge/${code}/test`);
        }).catch(err => {
          toast.error('Failed to start challenge');
          setCountdown(null);
          setStarting(false);
        });
      } else {
        // Non-creator: poll until status is "active", then redirect
        const pollForActive = async () => {
          try {
            const response = await challengeAPI.getByCode(code);
            if (response.data.status === 'active') {
              navigate(`/challenge/${code}/test`);
            } else {
              // Keep polling every 500ms
              setTimeout(pollForActive, 500);
            }
          } catch (err) {
            toast.error('Failed to join challenge');
            setCountdown(null);
          }
        };
        pollForActive();
      }
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, code, challenge?.is_creator, navigate]);

  const handleCopyLink = () => {
    const link = `${window.location.origin}/challenge/${code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleJoin = async () => {
  if (!isAuthenticated) {
    toast.error('Please login to join');
    navigate('/auth?mode=login&returnTo=/challenge/' + code);
    return;
  }

  setJoining(true);
  try {
    await challengeAPI.join(code);
    toast.success('Joined challenge!');
    await fetchChallenge(); // Add await here
  } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to join'));
  } finally {
    setJoining(false);
  }
};

  const handleStartChallenge = async () => {
    if (!challenge?.is_creator) return;
    
    setStarting(true);
    try {
      await challengeAPI.start(code);
      setCountdown(5);
      toast.success('Challenge starting!');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to start challenge'));
      setStarting(false);
    }
  };

  const handleDeleteChallenge = async () => {
    if (!window.confirm('Are you sure you want to cancel this challenge?')) return;
    
    try {
      await challengeAPI.cancel(code);
      toast.success('Challenge cancelled');
      navigate('/challenges');
    } catch (err) {
      toast.error('Failed to cancel challenge');
    }
  };

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16">
          <LoadingSpinner text="Loading challenge..." />
        </div>
      </Layout>
    );
  }

  // Error state
  if (error) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
          <h1 className="text-2xl font-bold text-foreground dark:text-white mb-4">
            Challenge Not Found
          </h1>
          <p className="text-muted-foreground mb-8">{error}</p>
          <Button onClick={() => navigate('/challenges')}>
            Back to Challenges
          </Button>
        </div>
      </Layout>
    );
  }

  // Countdown overlay
  if (countdown !== null) {
    return (
      <Layout>
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="mb-8">
              <Swords className="text-purple-400 mx-auto mb-4 animate-pulse" size={64} />
              <h2 className="text-3xl font-bold text-white mb-2">
                Challenge Starting!
              </h2>
              <p className="text-purple-300">Get ready...</p>
            </div>
            <div className="text-9xl font-bold text-purple-400 animate-bounce">
              {countdown}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // User submitted but challenge still active - waiting for others
  if (challenge.status === 'active' && challenge.user_status === 'completed') {
    const completedCount = (challenge.participants || []).filter(p => p.status === 'completed').length;
    const totalCount = (challenge.participants || []).length;
    
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="mb-8">
            <CheckCircle2 className="text-green-500 mx-auto mb-4" size={64} />
            <h1 className="text-3xl font-bold text-foreground dark:text-white mb-2">
              You've Submitted!
            </h1>
            <p className="text-muted-foreground mb-2">Your Score: <span className="text-2xl font-bold text-primary">{challenge.user_score}%</span></p>
            <p className="text-muted-foreground">Waiting for other players to finish...</p>
          </div>
          
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Loader2 className="animate-spin text-primary" size={24} />
              <span className="text-lg font-medium text-foreground">{completedCount}/{totalCount} players finished</span>
            </div>
            
            <div className="space-y-2">
              {(challenge.participants || []).map((p, idx) => (
                <div key={p.user_id} className={`flex items-center justify-between px-4 py-2 rounded-lg ${
                  p.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted/50'
                }`}>
                  <span className="font-medium text-foreground">{p.name}</span>
                  <Badge className={p.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}>
                    {p.status === 'completed' ? `${p.score}%` : 'In Progress'}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>
          
          <Button variant="outline" onClick={() => navigate('/challenges')}>
            Back to Challenges
          </Button>
        </div>
      </Layout>
    );
  }

  // Challenge completed - show results
  if (challenge.status === 'completed') {
    const sortedParticipants = [...(challenge.participants || [])].sort(
      (a, b) => (b.score || 0) - (a.score || 0)
    );

    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Card className="p-8 bg-card rounded-3xl border border-border">
            <div className="text-center mb-8">
              <Trophy className="text-yellow-500 mx-auto mb-4" size={64} />
              <h1 className="text-3xl font-bold text-foreground dark:text-white mb-2">
                Challenge Complete!
              </h1>
              <p className="text-muted-foreground">{challenge.title || challenge.exam_name}</p>
            </div>

            {/* Leaderboard */}
            <div className="space-y-3">
              {sortedParticipants.map((participant, idx) => (
                <div
                  key={participant.user_id}
                  className={`flex items-center gap-4 p-4 rounded-xl ${
                    idx === 0 ? 'bg-yellow-100 dark:bg-yellow-900/30 border-2 border-yellow-400' :
                    idx === 1 ? 'bg-gray-100 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600' :
                    idx === 2 ? 'bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-600' :
                    'bg-muted/50 border border-border'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                    idx === 0 ? 'bg-yellow-500 text-yellow-900' :
                    idx === 1 ? 'bg-gray-400 text-gray-900' :
                    idx === 2 ? 'bg-orange-400 text-orange-900' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground dark:text-white">
                      {participant.name}
                      {participant.user_id === user?.id && ' (You)'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {participant.correct || 0}/{participant.total || challenge.num_questions} correct
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-foreground dark:text-white">
                      {participant.score || 0}%
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Button onClick={() => navigate('/challenges')} className="gap-2">
                <Swords size={18} />
                Back to Challenges
              </Button>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  // Main lobby view
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Challenge Info Card */}
        <Card className="p-8 bg-card rounded-3xl border border-border mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 mb-2">
                {challenge.status === 'waiting' ? '⏳ Waiting for players' : 
                 challenge.status === 'active' ? '🔴 In Progress' : challenge.status}
              </Badge>
              <h1 className="text-3xl font-bold text-foreground dark:text-white">
                {challenge.title || challenge.exam_name}
              </h1>
              <p className="text-muted-foreground mt-1">
                Created by {challenge.creator_name}
                {challenge.is_creator && ' (You)'}
              </p>
            </div>
            <div className="text-center">
              <div className="font-mono text-3xl font-bold text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 px-4 py-2 rounded-xl">
                {code}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Share this code</p>
            </div>
          </div>

          {/* Challenge Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 rounded-xl bg-muted/50 border border-border">
              <Zap className="text-yellow-500 mx-auto mb-2" size={24} />
              <p className="text-2xl font-bold text-foreground dark:text-white">{challenge.num_questions}</p>
              <p className="text-xs text-muted-foreground">Questions</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-muted/50 border border-border">
              <Clock className="text-blue-500 mx-auto mb-2" size={24} />
              <p className="text-2xl font-bold text-foreground dark:text-white">{challenge.duration_minutes}</p>
              <p className="text-xs text-muted-foreground">Minutes</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-muted/50 border border-border">
              <Users className="text-green-500 mx-auto mb-2" size={24} />
              <p className="text-2xl font-bold text-foreground dark:text-white">{challenge.participants?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Players</p>
            </div>
          </div>

          {/* Share Section */}
          <div className="flex gap-2 mb-6">
            <Button onClick={handleCopyLink} variant="outline" className="flex-1 gap-2">
              {copied ? <Check size={18} className="text-green-500" /> : <Share2 size={18} />}
              Copy Link
            </Button>
            <Button onClick={handleCopyCode} variant="outline" className="flex-1 gap-2">
              {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
              Copy Code
            </Button>
          </div>

          {/* Action Buttons */}
          {!challenge.has_joined && challenge.status === 'waiting' && (
            <Button
              onClick={handleJoin}
              disabled={joining}
              className="w-full gap-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
              size="lg"
            >
              {joining ? <Loader2 className="animate-spin" size={20} /> : <Users size={20} />}
              Join Challenge
            </Button>
          )}

          {challenge.is_creator && challenge.status === 'waiting' && (
            <div className="flex gap-3 mt-4">
              <Button
                onClick={handleStartChallenge}
                disabled={starting || (challenge.participants?.length || 0) < 1}
                className="flex-1 gap-2 bg-gradient-to-r from-purple-600 to-pink-600"
                size="lg"
              >
                {starting ? <Loader2 className="animate-spin" size={20} /> : <Play size={20} />}
                Start Challenge
              </Button>
              <Button
                onClick={handleDeleteChallenge}
                variant="destructive"
                size="lg"
              >
                Cancel
              </Button>
            </div>
          )}

          {challenge.has_joined && !challenge.is_creator && challenge.status === 'waiting' && (
            <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-center">
              <Timer className="text-blue-500 mx-auto mb-2 animate-pulse" size={32} />
              <p className="text-blue-800 dark:text-blue-300 font-medium">
                Waiting for {challenge.creator_name} to start the challenge...
              </p>
            </div>
          )}
        </Card>

        {/* Participants List */}
        <Card className="p-6 bg-card rounded-2xl border border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-foreground dark:text-white flex items-center gap-2">
              <Users size={24} />
              Players ({challenge.participants?.length || 0})
            </h2>
            <Button onClick={fetchChallenge} variant="ghost" size="icon">
              <RefreshCw size={18} />
            </Button>
          </div>

          <div className="space-y-3">
            {(challenge.participants || []).map((participant, idx) => (
              <div
                key={participant.user_id}
                className={`flex items-center gap-4 p-3 rounded-xl ${
                  participant.user_id === challenge.creator_id
                    ? 'bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800'
                    : 'bg-muted/50 border border-border'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                  {participant.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-foreground dark:text-white flex items-center gap-2">
                    {participant.name}
                    {participant.user_id === challenge.creator_id && (
                      <Crown size={16} className="text-yellow-500" />
                    )}
                    {participant.user_id === user?.id && (
                      <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs">
                        You
                      </Badge>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Joined {new Date(participant.joined_at).toLocaleTimeString()}
                  </p>
                </div>
                <Badge className={`${
                  participant.status === 'waiting' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                  participant.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                }`}>
                  {participant.status === 'waiting' ? '⏳ Ready' : 
                   participant.status === 'active' ? '📝 Testing' : '✅ Done'}
                </Badge>
              </div>
            ))}

            {(!challenge.participants || challenge.participants.length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="mx-auto mb-2" size={32} />
                <p>No players yet. Share the code!</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default ChallengeLobby;
